import { useEffect, useRef } from 'react';
import { Outlet, useParams, useNavigate, useMatch } from 'react-router-dom';
import { useOrganization } from '@/contexts/OrganizationContext';
import { Loader2 } from 'lucide-react';
import { useRenderDebug } from '@/hooks/useRenderDebug';
import { useOrgTheme } from '@/hooks/useOrgTheme';
import { BookkeeperChatProvider } from '@/components/bookkeeper/BookkeeperChatProvider';
import { BookkeeperPanel } from '@/components/bookkeeper/BookkeeperPanel';

/**
 * Layout component that validates org access from URL and syncs with context.
 * Wraps all org-scoped routes under /org/:orgId
 *
 * IMPORTANT: This component keeps children mounted during loading/validation
 * to prevent modals and other state from being lost on context refreshes.
 */
export function OrgLayout() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { organizations, setSelectedOrganizationById, loading } = useOrganization();

  // The docked panel is a flex sibling of every org route, and `isOpen` is
  // restored from localStorage — so on the phone-shaped /apps/* surfaces it
  // would render a 400px-wide chat beside a 390px screen, and on
  // /apps/bookkeeper it would render the chat a second time. Those surfaces
  // are their own shell (docs/MOBILE.md "Scope"); suppress it there.
  const inAppsShell = useMatch('/org/:orgId/apps/*') !== null;

  // Apply org-specific theme preset via data-theme attribute on <html>
  useOrgTheme();

  // DEBUG: Track what's causing re-renders
  useRenderDebug('OrgLayout', {
    orgId,
    navigate,
    organizations,
    setSelectedOrganizationById,
    loading,
  });

  // Track if we've ever validated access - once validated, keep children mounted
  const hasValidatedRef = useRef(false);

  useEffect(() => {
    if (loading) return;

    if (!orgId) {
      // No org in URL - redirect to org select
      navigate('/org-select', { replace: true });
      return;
    }

    // Check if user has access to this org
    const hasAccess = organizations.some(org => org.id === orgId);

    if (!hasAccess && organizations.length > 0) {
      // User doesn't have access to this org - 404
      navigate('/404', { replace: true });
      return;
    }

    if (organizations.length === 0) {
      // User has no orgs - redirect to org select to create one
      navigate('/org-select', { replace: true });
      return;
    }

    // Sync context with URL and mark as validated
    setSelectedOrganizationById(orgId);
    hasValidatedRef.current = true;
  }, [orgId, organizations, loading, navigate, setSelectedOrganizationById]);

  // On initial load, show loading spinner
  // But once we've validated access, keep showing children during subsequent loads
  // This prevents modals from closing when context refreshes
  if (loading && !hasValidatedRef.current) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Check access - but if we've previously validated, keep showing children
  // This prevents brief unmounts during state transitions
  const hasAccess = organizations.some(org => org.id === orgId);
  if (!hasAccess && !hasValidatedRef.current) {
    return null;
  }

  // Docked assistant panel lives here (OrgLayout is stable across accounting
  // navigation, so chat state survives page changes). The panel self-gates on
  // the `bookkeeper_ai` feature flag and renders nothing when closed/disabled.
  return (
    <BookkeeperChatProvider>
      <div className="flex h-full w-full">
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
        {!inAppsShell && <BookkeeperPanel />}
      </div>
    </BookkeeperChatProvider>
  );
}
